import { CONTEXT_KEY } from "@atoms/atom-types";
import { Entities } from "../enums/entities";
import {
  categoryHasKeyCount,
  defaultProductTypeForCategory,
  InstrumentCategory,
  isInstrumentCategory,
} from "../enums/InstrumentCategory";
import type { IInstrumentInfo } from "../interfaces/IInstrumentInfo";
import { buildInstrumentKey, isInstrumentId } from "./instrumentKey";

type ImportField =
  | "id"
  | "category"
  | "manufacturer"
  | "model"
  | "keyCount"
  | "width"
  | "height"
  | "depth"
  | "productType"
  | "availableExternally";

/**
 * Normalises a column name so spelling variants meet: lower case, umlauts
 * spelled out, everything but letters and digits dropped, a trailing unit
 * "mm" removed. "Länge (mm)", "Laenge" and "laenge" all become "laenge"; a
 * header whose umlaut was lost to a wrong encoding ("L�nge") becomes "lnge",
 * which is listed as an alias too.
 */
export function normalizeInstrumentHeader(header: string): string {
  const spelled = header
    .toLowerCase()
    .replaceAll("ä", "ae")
    .replaceAll("ö", "oe")
    .replaceAll("ü", "ue")
    .replaceAll("ß", "ss")
    .replaceAll(/[^a-z0-9]/g, "");
  return spelled.length > 2 && spelled.endsWith("mm")
    ? spelled.slice(0, -2)
    : spelled;
}

/**
 * Accepted column names per field, already normalised. Covers our own
 * camelCase fields and German export labels, and the old keyboard database's
 * CSV (Hersteller, Modell, Tasten-Anzahl, Länge, Breite, Höhe, Aktiv), whose
 * "Länge" is our depth.
 */
const FIELD_ALIASES: Record<ImportField, readonly string[]> = {
  id: ["id", "uuid"],
  category: ["category", "kategorie"],
  manufacturer: ["manufacturer", "hersteller"],
  model: ["model", "modell"],
  keyCount: ["keycount", "tastenanzahl", "tasten", "anzahltasten"],
  width: ["width", "breite"],
  height: ["height", "hoehe", "hhe", "hohe"],
  depth: ["depth", "tiefe", "laenge", "lnge", "lange"],
  productType: ["producttype", "casetyp", "casetype", "gehaeusetyp"],
  availableExternally: [
    "availableexternally",
    "imexternenkonfiguratorverfuegbar",
    "imexternenkonfiguratorverfgbar",
    "active",
    "aktiv",
    "status",
  ],
};

const FIELD_BY_ALIAS = new Map<string, ImportField>(
  (Object.entries(FIELD_ALIASES) as [ImportField, readonly string[]][]).flatMap(
    ([field, aliases]) => aliases.map((alias) => [alias, field] as const),
  ),
);

/** The columns an import cannot do without, by their German names. */
const REQUIRED_FIELDS: readonly [ImportField, string][] = [
  ["manufacturer", "Hersteller"],
  ["model", "Modell"],
];

/**
 * The required columns (by German name) none of the headers stand for, so an
 * import can say what is missing before it sends anything.
 */
export function findMissingInstrumentHeaders(
  headers: readonly string[],
): string[] {
  const present = new Set(
    headers
      .map((header) => FIELD_BY_ALIAS.get(normalizeInstrumentHeader(header)))
      .filter((field): field is ImportField => field !== undefined),
  );
  return REQUIRED_FIELDS.filter(([field]) => !present.has(field)).map(
    ([, label]) => label,
  );
}

/**
 * Reads a number written either way: `370`, `370.5`, German `370,50`,
 * `1.005,00` with a thousands dot, or with a trailing `mm`.
 */
export function parseGermanNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string") return null;
  let text = value.trim().replace(/mm$/i, "").replaceAll(/\s/g, "");
  if (!text) return null;
  if (text.includes(",")) {
    text = text.replaceAll(".", "").replace(",", ".");
  }
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

const TRUE_WORDS = new Set(["1", "true", "ja", "yes", "x", "aktiv", "✓", "✔"]);
const FALSE_WORDS = new Set(["0", "false", "nein", "no", "inaktiv", ""]);

/** Reads an availability flag; `null` when the value says neither. */
export function parseAvailabilityFlag(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value !== "string") return null;
  const word = value.trim().toLowerCase();
  if (TRUE_WORDS.has(word)) return true;
  if (FALSE_WORDS.has(word)) return false;
  return null;
}

function asText(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Collects the known fields of a flat record, whatever the columns are called. */
function readFields(
  record: Record<string, unknown>,
): Partial<Record<ImportField, unknown>> {
  const fields: Partial<Record<ImportField, unknown>> = {};
  for (const [key, value] of Object.entries(record)) {
    const field = FIELD_BY_ALIAS.get(normalizeInstrumentHeader(key));
    if (field && fields[field] === undefined) {
      fields[field] = value;
    }
  }
  return fields;
}

/**
 * Case types are stored as `LTProducts` values ("keyboardcase"). A file may
 * carry the label instead ("Keyboard-Case"), which lower-cased and without
 * separators is the value for most types.
 */
function normalizeProductType(value: unknown): string {
  return asText(value)
    .toLowerCase()
    .replaceAll(/[\s_-]/g, "");
}

export interface ImportedInstrument {
  entry: IInstrumentInfo;
  /** The entity id the row came with, when it has a usable one. */
  id?: string;
}

/**
 * Turns one imported row into an instrument, or `null` when it names no
 * manufacturer or model.
 *
 * Accepts rows of the old keyboard database's CSV, of our own CSV export
 * (German labels) and JSON export (`{ context, atom }`), and of the
 * configurator file `instruments.json` (`innerDimensions`). Anything missing
 * falls back to a sensible default: category Keyboard, the category's case
 * type, not available externally, dimensions 0.
 */
export function toInstrumentInfo(raw: unknown): ImportedInstrument | null {
  if (!isRecord(raw)) return null;

  const atom = isRecord(raw.atom) ? raw.atom : raw;
  const fields = readFields(atom);
  if (isRecord(atom.innerDimensions)) {
    const inner = readFields(atom.innerDimensions);
    fields.width ??= inner.width;
    fields.height ??= inner.height;
    fields.depth ??= inner.depth;
  }

  const manufacturer = asText(fields.manufacturer);
  const model = asText(fields.model);
  if (!manufacturer || !model) return null;

  const categoryText = asText(fields.category);
  const category = isInstrumentCategory(categoryText)
    ? categoryText
    : InstrumentCategory.Keyboard;

  const keyCountValue = parseGermanNumber(fields.keyCount);
  const keyCount =
    categoryHasKeyCount(category) && keyCountValue !== null && keyCountValue > 0
      ? Math.round(keyCountValue)
      : null;

  const dimension = (value: unknown) => {
    const parsed = parseGermanNumber(value);
    return parsed !== null && parsed > 0 ? parsed : 0;
  };

  const entry: IInstrumentInfo = {
    category,
    manufacturer,
    model,
    keyCount,
    width: dimension(fields.width),
    height: dimension(fields.height),
    depth: dimension(fields.depth),
    productType:
      normalizeProductType(fields.productType) ||
      defaultProductTypeForCategory(category),
    availableExternally:
      parseAvailabilityFlag(fields.availableExternally) ?? false,
    lookupKey: buildInstrumentKey(manufacturer, model),
    deleted: false,
  };

  const context = isRecord(raw.context) ? raw.context : {};
  const id = [context[CONTEXT_KEY(Entities.Instrument)], raw.id, fields.id]
    .map(asText)
    .find(isInstrumentId);

  return id ? { entry, id: id.toLowerCase() } : { entry };
}
